import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import { 
  Switch, 
  Select, 
  Input, 
  Button, 
  Upload, 
  message, 
  Typography, 
  Space, 
  ColorPicker, 
  Tooltip, 
  Modal, 
  Slider, 
  Alert 
} from 'antd';
import { 
  UploadOutlined, 
  PlusOutlined, 
  SaveOutlined, 
  UserOutlined, 
  BankOutlined, 
  FileTextOutlined, 
  TableOutlined, 
  InfoCircleOutlined, 
  DeleteOutlined, 
  MinusOutlined 
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import api from '../api/axios';
import { getUserSettings, updateUserSettings } from '../api/settingsApi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styled from 'styled-components';
import dayjs from 'dayjs';
const { Option } = Select;
const { Title, Text } = Typography;

const userid = localStorage.getItem('userid');
// console.log(userid);

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const LoadingCircle = styled(motion.div)`
  width: 15px;
  height: 15px;
  margin: 0 5px;
  border-radius: 50%;
  background: #60a5fa;
  display: inline-block;
`;


// Animation Variants
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.075,
      type: 'spring',
      stiffness: 100,
      damping: 10
    }
  })
};

const AnimatedWrapper = React.memo(({ children }) => (
  <motion.div 
    initial={{ opacity: 0 }} 
    animate={{ opacity: 1 }} 
    transition={{ duration: 0.5 }}
    className="max-w-7xl mx-auto p-4 sm:p-8 space-y-6 sm:space-y-10 font-roboto bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen"
  >
    <motion.div 
      variants={itemVariants} 
      custom={1} 
      initial="hidden"
      animate="visible"
      className="mb-6 sm:mb-8 bg-white p-3 sm:p-6 rounded-2xl shadow-lg border border-blue-300"
    >
      {children}
    </motion.div>
  </motion.div>
));

const SettingsSection = React.memo(({ title, children, icon }) => (
  <div className="bg-white p-8 rounded-2xl shadow-lg mb-8 border border-gray-200">
    <Title level={3} className="flex items-center mb-6 text-gray-800">
      {icon}
      <span className="ml-3">{title}</span>
    </Title>
    {children}
  </div>
));

// Helper function for tooltips
const renderLabel = (label, tooltip) => (
  <div className="flex items-center">
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
    </Tooltip>
  </div>
);

// Image Crop Modal for profile photos
const ImageCropModal = React.memo(({ 
  visible, 
  onCancel, 
  onOk, 
  imageUrl
}) => {
  const [crop, setCrop] = useState({ 
    unit: 'px',
    width: 400,
    height: 400,
    x: 0,
    y: 0
  });
  const [scale, setScale] = useState(1);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.target;
    const cropSize = Math.min(width, height, 400);
    const x = (width - cropSize) / 2;
    const y = (height - cropSize) / 2;

    const initialCrop = { 
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: x,
      y: y
    };

    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }, []);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = 150; // Fixed output size for profile photos
    canvas.height = 150;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 1);
    });
  }, [completedCrop]);

  const handleOk = async () => {
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      onOk(croppedBlob);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <UserOutlined />
          <span>Adjust Profile Photo</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      width={800}
      className="image-crop-modal"
      okText="Save Photo"
      cancelText="Cancel"
    >
      <div className="p-4">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <InfoCircleOutlined />
            <p className="font-medium">Final image will be resized to 150 x 150 pixels</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <p>Drag to reposition the crop area. Use the zoom slider to adjust the image size.</p>
          </div>
        </div>
        <div className="mb-4">
          <Text className="block mb-2">Image Size</Text>
          <div className="flex items-center gap-4">
            <Button 
              icon={<MinusOutlined />} 
              onClick={() => setScale(s => Math.max(0.1, s - 0.01))}
              disabled={scale <= 0.1}
            />
            <Slider
              className="flex-1"
              min={0.1}
              max={3}
              step={0.01}
              value={scale}
              onChange={setScale}
              tooltip={{
                formatter: value => `${Math.round(value * 100)}%`
              }}
            />
            <Button 
              icon={<PlusOutlined />} 
              onClick={() => setScale(s => Math.min(3, s + 0.01))}
              disabled={scale >= 3}
            />
          </div>
          <Text className="text-xs text-gray-500 mt-1">
            Tip: Use the slider or buttons for 1% increments
          </Text>
        </div>
        <div className="max-h-[600px] overflow-auto bg-gray-50 rounded-lg p-4">
          <div className="relative">
            {imageUrl && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                locked={true}
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop preview"
                  className="max-w-full"
                  style={{ 
                    maxHeight: '600px',
                    transform: `scale(${scale})`,
                    transformOrigin: 'center'
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
});

// Logo Crop Modal
const LogoCropModal = React.memo(({ 
  visible, 
  onCancel, 
  onOk, 
  imageUrl
}) => {
  const [crop, setCrop] = useState({ 
    unit: 'px',
    width: 600,
    height: 200,
    x: 0,
    y: 0
  });
  const [scale, setScale] = useState(1);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    let cropWidth = width;
    let cropHeight = cropWidth / 3;
    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = cropHeight * 3;
    }

    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;

    const initialCrop = {
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight,
    };

    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }, []);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = 450; 
    canvas.height = 150;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 1);
    });
  }, [completedCrop]);

  const handleOk = async () => {
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        await onOk(croppedBlob);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      message.error('Failed to process image');
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <BankOutlined />
          <span>Adjust Company Logo</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      width={1000}
      className="logo-crop-modal"
      okText="Save Logo"
      cancelText="Cancel"
    >
      <div className="p-4">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <InfoCircleOutlined />
            <p className="font-medium">Final logo will be resized to 450 x 150 pixels</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <p>Drag to reposition the crop area. Use the zoom slider to adjust the image size.</p>
          </div>
        </div>

        <div className="mb-4">
          <Text className="block mb-2">Image Size</Text>
          <div className="flex items-center gap-4">
            <Button 
              icon={<MinusOutlined />} 
              onClick={() => setScale(s => Math.max(0.1, s - 0.01))}
              disabled={scale <= 0.1}
            />
            <Slider
              className="flex-1"
              min={0.1}
              max={3}
              step={0.01}
              value={scale}
              onChange={setScale}
              tooltip={{
                formatter: value => `${Math.round(value * 100)}%`
              }}
            />
            <Button 
              icon={<PlusOutlined />} 
              onClick={() => setScale(s => Math.min(3, s + 0.01))}
              disabled={scale >= 3}
            />
          </div>
        </div>

        <div className="max-h-[600px] overflow-auto bg-gray-50 rounded-lg p-4">
          <div className="relative">
            {imageUrl && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={4/3}
                locked={true}
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop preview"
                  className="max-w-full"
                  style={{ 
                    maxHeight: '600px',
                    transform: `scale(${scale})`,
                    transformOrigin: 'center'
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
});

const AdvisorItem = React.memo(({ advisor, index, onChange, availableDesignations }) => {
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const handleImageUpload = async (file) => {
    const isImage = file.type === 'image/png' || file.type === 'image/jpeg';
    if (!isImage) {
      message.error('You can only upload PNG/JPEG files!');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }

    const fileUrl = URL.createObjectURL(file);
    setUploadFile(file);
    setTempImageUrl(fileUrl);
    setCropModalVisible(true);

    return false;
  };

  const handleCropComplete = async (croppedBlob) => {
    const formData = new FormData();
    formData.append('file', new File([croppedBlob], uploadFile.name, { type: 'image/jpeg' }));

    try {
      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/upload_advisor_image/${userid}`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok) {
        message.success(`Profile photo uploaded successfully`);
        onChange(advisor.id, 'image', `images/${userid}/advisors/${data.filename}`);
      } else {
        message.error('Failed to upload profile photo');
      }
    } catch (error) {
      message.error('Error uploading profile photo');
    }

    setCropModalVisible(false);
    setTempImageUrl(null);
    setUploadFile(null);
  };

  return (
    <div className="p-6 rounded-xl border border-gray-200 shadow-lg">
      <Title level={5} className="mb-4">Advisor {index + 1}</Title>
      <Space direction="vertical" size="middle" className="w-full">
        <div>
          <Text strong>
            {renderLabel("Name", "Enter the advisor's full name as it should appear on documents")}
          </Text>
          <Input
            placeholder="Enter advisor name"
            value={advisor.name || ''}
            onChange={(e) => onChange(advisor.id, 'name', e.target.value)}
            className="mt-2"
          />
        </div>
        
        <div>
          <Text strong>
            {renderLabel("Email", "Enter the advisor's contact email")}
          </Text>
          <Input
            placeholder="Enter advisor email"
            value={advisor.email || ''}
            onChange={(e) => onChange(advisor.id, 'email', e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <Text strong>
            {renderLabel("Phone", "Enter the advisor's contact phone number")}
          </Text>
          <Input
            placeholder="Enter advisor phone"
            value={advisor.phone || ''}
            onChange={(e) => onChange(advisor.id, 'phone', e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <Text strong>
            {renderLabel("Designations", "Select all professional designations held by this advisor")}
          </Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select designations"
            value={advisor.designations}
            onChange={(value) => onChange(advisor.id, 'designations', value)}
            className="mt-2"
          >
            {availableDesignations.map(designation => (
              <Option key={designation.value} value={designation.value}>
                {designation.label}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <Text strong>
            {renderLabel("Profile Image", "Upload the advisor's profile image")}
          </Text>
          <div className="mt-4">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/png, image/jpeg"
              customRequest={() => {}}
            >
              <div className="flex flex-col items-start">
                <div className="mx-auto">
                  {advisor.image ? (
                    <div className="relative group cursor-pointer">
                      <div className="w-[150px] h-[150px] overflow-hidden border-4 border-white shadow-lg transition-transform duration-300 transform group-hover:scale-105">
                        <img
                          src={`https://plansync-backend-234288438538.us-central1.run.app/${advisor.image}`}
                          alt={`${advisor.name || 'Advisor'}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Image failed to load:', e.target.src);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDE1MCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjE1MCIgaGVpZ2h0PSIxNTAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9InN5c3RlbS11aSwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4Ij5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                            message.error('Could not load advisor profile image');
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
                          <UploadOutlined className="text-white text-2xl mb-2" />
                          <span className="text-white text-sm font-medium">Change Photo</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center border-4 border-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                      <div className="text-center transform group-hover:scale-110 transition-transform duration-300">
                        <UserOutlined className="text-4xl text-gray-400 mb-2" />
                        <div className="text-sm font-medium text-gray-500">Upload Photo</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col gap-2 mt-4 w-full">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      type="default"
                      className="flex items-center px-6 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all duration-300 w-full"
                    >
                      <UploadOutlined className="mr-2" />
                      {advisor.image ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                  </motion.div>
                  
                  {advisor.image && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        danger
                        className="flex items-center px-6 py-2 border-red-100 rounded-full shadow-sm hover:shadow-md transition-all duration-300 w-full text-red-500 hover:text-red-600 hover:border-red-100 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange(advisor.id, 'image', '');
                        }}
                      >
                        <DeleteOutlined className="mr-2" />
                        Remove Photo
                      </Button>
                    </motion.div>
                  )}
                </div>
              </div>
            </Upload>
            <ImageCropModal
              visible={cropModalVisible}
              onCancel={() => {
                setCropModalVisible(false);
                setTempImageUrl(null);
                setUploadFile(null);
              }}
              onOk={handleCropComplete}
              imageUrl={tempImageUrl}
            />
          </div>
        </div>
      </Space>
    </div>
  );
});

const AdvisorSettings = React.memo(({ advisors, handleAdvisorChange, availableDesignations, addAdvisor }) => (
  <SettingsSection title="Advisor Information" icon={<UserOutlined />}>
    <Space direction="vertical" size="large" className="w-full">
      {advisors.map((advisor, index) => (
        <AdvisorItem
          key={advisor.id}
          advisor={advisor}
          index={index}
          onChange={handleAdvisorChange}
          availableDesignations={availableDesignations}
        />
      ))}
      <Button 
        icon={<PlusOutlined />}
        onClick={addAdvisor}
        className="w-full"
        type="dashed"
      >
        Add New Advisor
      </Button>
    </Space>
  </SettingsSection>
));

const CompanySettings = React.memo(({ 
  companyName, 
  companyAddress, 
  companyPhone, 
  companyWebsite, 
  disclaimer,
  handleChange,
  logoPath
}) => {
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const handleLogoUpload = async (file) => {
    const isImage = file.type === 'image/png' || file.type === 'image/jpeg';
    if (!isImage) {
      message.error('You can only upload PNG/JPEG files!');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }

    const fileUrl = URL.createObjectURL(file);
    setUploadFile(file);
    setTempImageUrl(fileUrl);
    setCropModalVisible(true);
    return false;
  };

  const handleCropComplete = async (croppedBlob) => {
    const formData = new FormData();
    formData.append('file', new File([croppedBlob], uploadFile.name, { type: 'image/jpeg' }));
    const userid = localStorage.getItem('userid');

    try {
      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/upload_logo_image/${userid}`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok) {
        message.success('Logo uploaded successfully');
        handleChange('logoPath', `images/${userid}/logo/${data.filename}`);
      } else {
        message.error('Failed to upload logo');
      }
    } catch (error) {
      message.error('Error uploading logo');
    }

    setCropModalVisible(false);
    setTempImageUrl(null);
    setUploadFile(null);
  };

  return (
    <SettingsSection title="Company Information" icon={<BankOutlined />}>
      <Space direction="vertical" size="large" className="w-full">
        <div>
          <Text strong>
            {renderLabel("Company Logo", "Upload your company logo (Recommended: 400 x 300 pixels)")}
          </Text>
          <div className="mt-4">
            <Upload
              beforeUpload={handleLogoUpload}
              showUploadList={false}
              accept="image/png, image/jpeg"
              customRequest={() => {}}
            >
              <div className="flex flex-col items-start">
                <div className="mx-auto">
                  {logoPath ? (
                    <div className="relative group cursor-pointer">
                      <div className="w-[200px] h-[150px] overflow-hidden border-4 border-white shadow-lg transition-transform duration-300 transform group-hover:scale-105">
                        <img
                          src={`https://plansync-backend-234288438538.us-central1.run.app/${logoPath}`}
                          alt="Company Logo"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.error('Logo failed to load:', e.target.src);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxNTAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9InN5c3RlbS11aSwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4Ij5Mb2dvIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                            message.error('Could not load company logo');
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
                          <UploadOutlined className="text-white text-2xl mb-2" />
                          <span className="text-white text-sm font-medium">Change Logo</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-[200px] h-[150px] rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center border-4 border-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                      <div className="text-center transform group-hover:scale-110 transition-transform duration-300">
                        <BankOutlined className="text-4xl text-gray-400 mb-2" />
                        <div className="text-sm font-medium text-gray-500">Upload Logo</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col gap-2 mt-4 w-full">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      type="default"
                      className="flex items-center px-6 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all duration-300 w-full"
                    >
                      <UploadOutlined className="mr-2" />
                      {logoPath ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                  </motion.div>
                  
                  {logoPath && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        danger
                        className="flex items-center px-6 py-2 border-red-100 rounded-full shadow-sm hover:shadow-md transition-all duration-300 w-full text-red-500 hover:text-red-600 hover:border-red-100 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChange('logoPath', '');
                        }}
                      >
                        <DeleteOutlined className="mr-2" />
                        Remove Logo
                      </Button>
                    </motion.div>
                  )}
                </div>
              </div>
            </Upload>

            <LogoCropModal
              visible={cropModalVisible}
              onCancel={() => {
                setCropModalVisible(false);
                setTempImageUrl(null);
                setUploadFile(null);
              }}
              onOk={handleCropComplete}
              imageUrl={tempImageUrl}
            />
          </div>
        </div>

        <div>
          <Text strong>
            {renderLabel("Company Name", "Enter your company's legal business name")}
          </Text>
          <Input
            placeholder="Enter company name"
            value={companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <Text strong>Company Address</Text>
          <Space direction="vertical" size="middle" className="w-full">
            <Input
              placeholder="Street Address"
              value={companyAddress.street}
              onChange={(e) => handleChange('companyAddress', { ...companyAddress, street: e.target.value })}
            />
            <Input
              placeholder="Suite/Unit"
              value={companyAddress.suite}
              onChange={(e) => handleChange('companyAddress', { ...companyAddress, suite: e.target.value })}
            />
            <Input
              placeholder="City, State"
              value={companyAddress.cityState}
              onChange={(e) => handleChange('companyAddress', { ...companyAddress, cityState: e.target.value })}
            />
            <Input
              placeholder="ZIP Code"
              value={companyAddress.zip}
              onChange={(e) => handleChange('companyAddress', { ...companyAddress, zip: e.target.value })}
            />
          </Space>
        </div>

        <div>
          <Text strong>Contact Information</Text>
          <Space direction="vertical" size="middle" className="w-full">
            <Input
              placeholder="Company Phone Number"
              value={companyPhone}
              onChange={(e) => handleChange('companyPhone', e.target.value)}
            />
            <Input
              placeholder="Company Website"
              value={companyWebsite}
              onChange={(e) => handleChange('companyWebsite', e.target.value)}
            />
          </Space>
        </div>

        <div>
          <Text strong>
            {renderLabel("Company Disclaimer", "Enter your company's legal disclaimer text")}
          </Text>
          <Input.TextArea
            placeholder="Enter disclaimer text"
            value={disclaimer}
            onChange={(e) => handleChange('disclaimer', e.target.value)}
            rows={4}
            className="mt-2"
          />
        </div>
      </Space>
    </SettingsSection>
  );
});

const DocumentImageSettings = React.memo(({ 
  companyName,
  companyAddress,
  companyPhone,
  companyWebsite,
  disclaimer,
  logoPath,
  headerImage,
  footerImage,
  handleChange
}) => {
  return (
    <SettingsSection title="Document Images" icon={<FileTextOutlined />}>
      <Space direction="vertical" size="large" className="w-full">
        <div>
          <Text strong>Header Image</Text>
          <div className="space-y-4">
            {headerImage ? (
              <div className="mt-4">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={`https://plansync-backend-234288438538.us-central1.run.app/${headerImage}`}
                    alt="Document Header"
                    className="w-full object-contain mb-2"
                    onError={(e) => {
                      console.error('Header image failed to load:', e.target.src);
                      message.error('Could not load header image');
                    }}
                  />
                  <Text type="secondary" className="text-sm italic block">
                    This header was automatically generated using your company logo, address ({companyAddress.street}), 
                    phone number ({companyPhone}), and website ({companyWebsite}). 
                    It will update automatically when you save changes to these fields.
                  </Text>
                </div>
              </div>
            ) : (
              <Alert
                type="info"
                message="Header Image Not Generated"
                description={
                  <div>
                    <p>A header image will be automatically generated when you save your settings if you have provided:</p>
                    <ul className="list-disc ml-4 mt-2">
                      <li>Company Logo</li>
                      <li>Company Address</li>
                      <li>Phone Number</li>
                      <li>Website</li>
                    </ul>
                  </div>
                }
              />
            )}
          </div>
        </div>

        <div>
          <Text strong>Footer Image</Text>
          <div className="space-y-4">
            {footerImage ? (
              <div className="mt-4">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={`https://plansync-backend-234288438538.us-central1.run.app/${footerImage}`}
                    alt="Document Footer"
                    className="w-full object-contain mb-2"
                    onError={(e) => {
                      console.error('Footer image failed to load:', e.target.src);
                      message.error('Could not load footer image');
                    }}
                  />
                  <Text type="secondary" className="text-sm italic block">
                    This footer was automatically generated using your company disclaimer. 
                    It will update automatically when you save changes to the disclaimer text.
                  </Text>
                </div>
              </div>
            ) : (
              <Alert
                type="info"
                message="Footer Image Not Generated"
                description="A footer image will be automatically generated when you save your settings if you have provided a company disclaimer."
              />
            )}
          </div>
        </div>
      </Space>
    </SettingsSection>
  );
});

const ReportSettings = React.memo(({ colorScheme, handleChange }) => (
  <SettingsSection title="Report Settings" icon={<FileTextOutlined />}>
    <Text strong>
      {renderLabel("Color Scheme", "Choose a primary color for your reports and documents")}
    </Text>
    <div>
      <ColorPicker
        value={colorScheme}
        onChange={(color) => handleChange('colorScheme', color.toHexString())}
        trigger="hover"
        disabledAlpha
        presets={[
          {
            label: 'Recommended',
            colors: [
              '#4a90e2',
              '#50C878',
              '#FF6B6B',
              '#9B59B6',
              '#34495E',
              '#2ECC71',
              '#E74C3C',
              '#8E44AD'
            ],
          },
        ]}
      />
      <div style={{ marginTop: '16px' }}>
        <div style={{
          width: '100%',
          height: '60px',
          backgroundColor: colorScheme,
          borderRadius: '8px',
          marginBottom: '8px',
          border: '1px solid #e6e6e6'
        }} />
        <Text type="secondary">Preview of selected color</Text>
      </div>
    </div>
  </SettingsSection>
));

const SpreadsheetSettings = React.memo(({ enableAIRewriting, handleChange }) => (
  <SettingsSection title="Spreadsheet Settings" icon={<TableOutlined />}>
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2">
        <Text strong>
          {renderLabel(
            "Turn on AI Rewriter by Default",
            "When enabled, AI will by default help improve grammatical correctness and consistency in the touchpoints or requirements that you enter into your Plan Data"
          )}
        </Text>
      </div>
      <div className="flex justify-end pt-1">
        <Switch
          checked={enableAIRewriting}
          onChange={(checked) => handleChange('enableAIRewriting', checked)}
          className="scale-125"
        />
      </div>
    </div>
  </SettingsSection>
));

const AdminKeySettings = React.memo(({ userId }) => {
  const [adminKey, setAdminKey] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const adminSignupUrl = `https://app.plansync.ai/sign-up/admin`;

  const handleGenerateAdminKey = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      const response = await api.post('/api/auth/generate-admin-key', null, {
        params: { user_id: userId }
      });
      
      if (response.data && response.data.admin_key) {
        setAdminKey(response.data.admin_key);
        message.success('Admin key generated successfully');
      } else {
        throw new Error('Failed to generate admin key');
      }
    } catch (error) {
      console.error('Error generating admin key:', error);
      setError(error.message || 'Failed to generate admin key');
      message.error('Failed to generate admin key');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => message.success('Copied to clipboard'),
      () => message.error('Failed to copy to clipboard')
    );
  };

  return (
    <SettingsSection title="Admin Access" icon={<UserOutlined />}>
      <div className="space-y-6">
        <div>
          <Text strong>
            {renderLabel("Admin Key", "Generate a key that allows others to sign up as admins on your account")}
          </Text>
          <div className="mt-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <Button 
                type="primary"
                onClick={handleGenerateAdminKey}
                loading={isGenerating}
                className="w-full md:w-auto"
              >
                Generate Admin Key
              </Button>
              <Text type="secondary" className="text-sm">
                This allows other users to sign up with access to your data
              </Text>
            </div>

            {error && (
              <Alert 
                message="Error generating admin key" 
                description={error} 
                type="error" 
                showIcon 
              />
            )}

            {adminKey && (
              <div className="mt-4 space-y-4">
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex flex-col space-y-1">
                    <Text strong>Admin Key:</Text>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="bg-white p-2 border border-gray-300 rounded flex-grow overflow-x-auto">
                        <Text code copyable={false} className="text-xs sm:text-sm whitespace-nowrap">
                          {adminKey}
                        </Text>
                      </div>
                      <Button 
                        icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path d="M7.5 3.375c0-1.036.84-1.875 1.875-1.875h.375a3.75 3.75 0 013.75 3.75v1.875C13.5 8.161 14.34 9 15.375 9h1.875A3.75 3.75 0 0121 12.75v3.375C21 17.16 20.16 18 19.125 18h-9.75A1.875 1.875 0 017.5 16.125V3.375z" />
                          <path d="M15 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0017.25 7.5h-1.875A.375.375 0 0115 7.125V5.25zM4.875 6H6v10.125A3.375 3.375 0 009.375 19.5H16.5v1.125c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V7.875C3 6.839 3.84 6 4.875 6z" />
                        </svg>}
                        onClick={() => copyToClipboard(adminKey)}
                        className="flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex flex-col space-y-2">
                    <Text strong>Admin Signup URL:</Text>
                    <div className="flex items-center gap-2">
                      <div className="bg-white p-2 border border-gray-300 rounded flex-grow overflow-x-auto">
                        <Text code copyable={false} className="text-xs sm:text-sm whitespace-nowrap">
                          {adminSignupUrl}
                        </Text>
                      </div>
                      <Button 
                        icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path d="M7.5 3.375c0-1.036.84-1.875 1.875-1.875h.375a3.75 3.75 0 013.75 3.75v1.875C13.5 8.161 14.34 9 15.375 9h1.875A3.75 3.75 0 0121 12.75v3.375C21 17.16 20.16 18 19.125 18h-9.75A1.875 1.875 0 017.5 16.125V3.375z" />
                          <path d="M15 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0017.25 7.5h-1.875A.375.375 0 0115 7.125V5.25zM4.875 6H6v10.125A3.375 3.375 0 009.375 19.5H16.5v1.125c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V7.875C3 6.839 3.84 6 4.875 6z" />
                        </svg>}
                        onClick={() => copyToClipboard(adminSignupUrl)}
                        className="flex-shrink-0"
                      />
                    </div>
                    <Text type="secondary" className="text-sm mt-2">
                      Share this URL along with the admin key to allow others to sign up with access to your data.
                    </Text>
                  </div>
                </div>

                <Alert
                  message="Important Security Notice"
                  description="Only share this key with trusted individuals. Admins will have access to all your data and can make changes on your behalf."
                  type="warning"
                  showIcon
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </SettingsSection>
  );
});

const categories = [
  { name: 'All', icon: UserOutlined },
  { name: 'Advisor', icon: UserOutlined },
  { name: 'Company', icon: BankOutlined },
  { name: 'Report', icon: FileTextOutlined },
  { name: 'Spreadsheet', icon: TableOutlined },
];

const cleanSettingsForSave = (settings) => {
  // console.log("Settings being formatted:", settings);
  
  const formattedSettings = {
    advisor_name: settings.name || '',
    company_name: settings.companyName || '',
    color_scheme: settings.colorScheme || '#168bd4',
    selected_titles: Array.isArray(settings.designations) ? settings.designations.join(', ') : '',
    advisor_designations: Array.isArray(settings.designations) ? settings.designations.join(', ') : '',
    enable_ai_rewriting: Boolean(settings.enableAIRewriting),
    expand_mastersheet_filters_form_on_load: false,
    expand_auto_data_entry_form_on_load: false,
    expand_ai_rewriting_form_on_load: false,
    header_image: settings.headerImage || '',
    footer_image: settings.footerImage || '',
    logo: settings.logoPath || '',
    years: ['2024', '2025'],
    advisor1: settings.advisors?.[0] ? {
      Name: settings.advisors[0].name || '',
      Email: settings.advisors[0].email || '',
      Phone: settings.advisors[0].phone || '',
      Designations: settings.advisors[0].designations || [],
      "Profile Image": settings.advisors[0].image || ''
    } : {},
    advisor2: settings.advisors?.[1] ? {
      Name: settings.advisors[1].name || '',
      Email: settings.advisors[1].email || '',
      Phone: settings.advisors[1].phone || '',
      Designations: settings.advisors[1].designations || [],
      "Profile Image": settings.advisors[1].image || ''
    } : {},
    advisor3: settings.advisors?.[2] ? {
      Name: settings.advisors[2].name || '',
      Email: settings.advisors[2].email || '',
      Phone: settings.advisors[2].phone || '',
      Designations: settings.advisors[2].designations || [],
      "Profile Image": settings.advisors[2].image || ''
    } : {},
    company_phone: settings.companyPhone || '',
    company_email: settings.companyEmail || '',
    company_website: settings.companyWebsite || '',
    company_disclaimer: settings.disclaimer || '',
    company_address: settings.companyAddress ? 
      `${settings.companyAddress.street || ''}, ${settings.companyAddress.suite || ''}, ${settings.companyAddress.cityState || ''}, ${settings.companyAddress.zip || ''}` : ''
  };

  // console.log("Formatted settings:", formattedSettings);

  const cleanedSettings = Object.fromEntries(
    Object.entries(formattedSettings).filter(([key, value]) => {
      if (key.startsWith('advisor') && typeof value === 'object' && Object.keys(value).length === 0) {
        return false;
      }
      return value !== "string";
    })
  );

  // console.log("Cleaned settings:", cleanedSettings);
  return cleanedSettings;
};

const Settings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { isCustomerView } = useCompany();

  // Redirect customer users away from settings
  useEffect(() => {
    if (isCustomerView) {
      navigate('/data-dashboard');
    }
  }, [isCustomerView, navigate]);

  const [settings, setSettings] = useState({
    name: '',
    companyName: '',
    headerImage: '',
    footerImage: '',
    colorScheme: '',
    designations: [],
    advisors: [
      { id: 0, name: '', email: '', phone: '', designations: [], image: '' },
      { id: 1, name: '', email: '', phone: '', designations: [], image: '' },
      { id: 2, name: '', email: '', phone: '', designations: [], image: '' }
    ],
    companyAddress: { street: '', suite: '', cityState: '', zip: '' },
    companyPhone: '',
    companyWebsite: '',
    enableAIRewriting: false,
    activeTab: '',
    disclaimer: '',
    logoPath: '',
    yearImage: '' // Added
  });

  const [allDesignations] = useState([
    { value: "CFP", label: "CFP®️" },
    { value: "CFA", label: "CFA" },
    { value: "ChFC", label: "ChFC" },
    { value: "CIMA", label: "CIMA®️" },
    { value: "CPWA", label: "CPWA®️" },
    { value: "CEPA", label: "CEPA®️" },
    { value: "CLU", label: "CLU®️" },
    { value: "RICP", label: "RICP®️" },
    { value: "AIF", label: "AIF®️" },
    { value: "CRPC", label: "CRPC®️" }
  ]);

  const availableDesignations = useMemo(() => {
    return allDesignations.filter(designation => !settings.designations.includes(designation.value));
  }, [settings.designations, allDesignations]);

  const handleChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleAdvisorChange = useCallback((id, key, value) => {
    setSettings(prev => {
      const newAdvisors = prev.advisors.map(advisor => {
        if (advisor.id === id) {
          return { ...advisor, [key]: value };
        }
        return advisor;
      });
      return { ...prev, advisors: newAdvisors };
    });
  }, []);

  const addAdvisor = useCallback(() => {
    setSettings(prev => ({
      ...prev,
      advisors: [...prev.advisors, { id: Date.now(), name: '', designations: [] }],
    }));
  }, []);

  const handleFileUpload = useCallback((info, key) => {
    const { status, name } = info.file;
    if (status === 'done') {
      message.success(`${name} file uploaded successfully`);
      handleChange(key, info.file.response.url);
    } else if (status === 'error') {
      message.error(`${name} file upload failed.`);
    }
  }, [handleChange]);

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userid = localStorage.getItem('userid');
        if (!userid) {
          console.error('No user ID found');
          setIsLoading(false);
          return;
        }

        const userSettings = await getUserSettings(userid);
        
        if (userSettings) {
          // console.log('Loaded logo path:', userSettings.logo);
          
          const addressParts = userSettings.company_address.split(',').map(part => part.trim());
          const formattedAddress = {
            street: addressParts[0] || '',
            suite: addressParts[1] || '',
            cityState: addressParts[2] || '',
            zip: addressParts[3] || ''
          };

          const formattedAdvisors = [
            userSettings.advisor1,
            userSettings.advisor2,
            userSettings.advisor3
          ].filter(Boolean).map((advisor, index) => ({
            id: index + 1,
            name: advisor.Name,
            email: advisor.Email,
            phone: advisor.Phone,
            designations: advisor.Designations,
            image: advisor['Profile Image']
          }));

          setSettings({
            name: userSettings.advisor_name || '',
            companyName: userSettings.company_name || '',
            headerImage: userSettings.header_image || '',
            footerImage: userSettings.footer_image || '',
            colorScheme: userSettings.color_scheme || '#168bd4',
            designations: userSettings.selected_titles ? userSettings.selected_titles.split(', ') : [],
            advisors: formattedAdvisors,
            companyAddress: formattedAddress,
            companyPhone: userSettings.company_phone || '',
            companyWebsite: userSettings.company_website || '',
            enableAIRewriting: userSettings.enable_ai_rewriting ?? true,
            activeTab: 'all',
            disclaimer: userSettings.company_disclaimer || '',
            logoPath: userSettings.logo || '',
            yearImage: '' // Assuming the userSettings might have this in future
          });
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading settings:', error);
        setIsLoading(false);
        message.error('Failed to load settings');
      }
    };

    loadUserSettings();
  }, []);

  const handleSaveSettings = async () => {
    const loadingMessage = message.loading('Saving settings and generating document images...', 0);
    const userid = localStorage.getItem('userid');
    if (!userid) {
      loadingMessage();
      message.error('No user ID found');
      return;
    }

    const oldHeaderImage = settings.headerImage;
    const oldFooterImage = settings.footerImage;
    const oldLogoPath = settings.logoPath;
    const oldYearImage = settings.yearImage;

    // Example: If you want to generate a year image:
    if (settings.years && settings.years.length > 0) {
      // If the current year is not in the settings.years array, add it
      const currentYear = dayjs().year();
      if (!settings.years.includes(currentYear)) {
        settings.years.push(currentYear);
      }
      const year = settings.years[-1]; 
      try {
        const yearResponse = await api.post('/api/create_year_image', {
          year: year,
          output_path: `images/${userid}/year/${userid}_${year}.png`
        });

        if (yearResponse.data.message) {
          handleChange('yearImage', yearResponse.data.message);
        }
      } catch (error) {
        console.error('Error generating year image:', error);
        message.warning('Could not generate year image. Please ensure the year is correctly set.');
      }
    }

    try {
      if (settings.logoPath && settings.companyAddress && settings.companyPhone && settings.companyWebsite && settings.companyName) {
        const formattedAddress = `${settings.companyAddress.street}, ${settings.companyAddress.suite}, ${settings.companyAddress.cityState}, ${settings.companyAddress.zip}`;
        
        try {
          const headerResponse = await api.post('/api/create_header_image', {
            user_id: userid,
            logo_path: settings.logoPath,
            address: formattedAddress,
            phone: settings.companyPhone,
            website: settings.companyWebsite
          });

          if (headerResponse.data.image_path) {
            handleChange('headerImage', headerResponse.data.image_path);
          }
        } catch (error) {
          console.error('Error generating header image:', error);
          message.warning('Could not generate header image. Please ensure all company information is filled out correctly.');
        }
      }

      if (settings.disclaimer) {
        try {
          const footerResponse = await api.post('/api/create_footer_image', {
            user_id: userid,
            disclaimer_text: settings.disclaimer
          });

          if (footerResponse.data.image_path) {
            handleChange('footerImage', footerResponse.data.image_path);
          }
        } catch (error) {
          console.error('Error generating footer image:', error);
          message.warning('Could not generate footer image. Please ensure the disclaimer text is entered correctly.');
        }
      }

      const cleanedSettings = cleanSettingsForSave(settings);
      const response = await updateUserSettings(userid, cleanedSettings);
      
      loadingMessage();
      
      if (response) {
        message.success('Settings and document images saved successfully');

        // Delete old images if changed
        if (oldHeaderImage && oldHeaderImage !== settings.headerImage) {
          await api.post('/api/delete_file', { path: oldHeaderImage });
        }
        if (oldFooterImage && oldFooterImage !== settings.footerImage) {
          await api.post('/api/delete_file', { path: oldFooterImage });
        }
        if (oldLogoPath && oldLogoPath !== settings.logoPath) {
          await api.post('/api/delete_file', { path: oldLogoPath });
        }
        if (oldYearImage && oldYearImage !== settings.yearImage) {
          await api.post('/api/delete_file', { path: oldYearImage });
        }

      } else {
        message.error('Failed to save settings');
      }
    } catch (error) {
      loadingMessage();
      console.error('Error updating user settings:', error);
      message.error(error.message || 'An error occurred while saving settings');
    }
  };

  return (
    <AnimatedWrapper>
      {isLoading ? (
        <LoadingContainer>
          <div>
            {[0, 1, 2].map((index) => (
              <LoadingCircle
                key={index}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.6, 1, 0.6],
                  y: [0, -20, 0],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: index * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </LoadingContainer>
      ) : (
        <motion.div variants={itemVariants} custom={1} className="mb-6 sm:mb-8 bg-white p-3 sm:p-6 rounded-2xl">
          <SectionTitle>User Settings</SectionTitle>

          <motion.nav 
            variants={itemVariants} 
            className="flex flex-wrap justify-center gap-4 mb-12 p-8 bg-white shadow-xl rounded-2xl border border-blue-200"
          >
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.name}
                  onClick={() => handleChange('activeTab', category.name.toLowerCase())}
                  className={`flex items-center px-6 py-3 rounded-full transition-all duration-300 ${
                    settings.activeTab === category.name.toLowerCase()
                      ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className={`w-5 h-5 mr-2 ${
                    settings.activeTab === category.name.toLowerCase() 
                      ? 'text-white' 
                      : 'text-blue-500'
                  }`} />
                  <span className="text-sm font-medium">{category.name}</span>
                </button>
              );
            })}
          </motion.nav>

          {settings.activeTab === 'all' && (
            <>
              <AdvisorSettings
                advisors={settings.advisors}
                handleAdvisorChange={handleAdvisorChange}
                availableDesignations={availableDesignations}
                addAdvisor={addAdvisor}
              />
              <CompanySettings
                companyName={settings.companyName}
                companyAddress={settings.companyAddress}
                companyPhone={settings.companyPhone}
                companyWebsite={settings.companyWebsite}
                disclaimer={settings.disclaimer}
                handleChange={handleChange}
                handleFileUpload={handleFileUpload}
                logoPath={settings.logoPath}
              />
              <DocumentImageSettings
                companyName={settings.companyName}
                companyAddress={settings.companyAddress}
                companyPhone={settings.companyPhone}
                companyWebsite={settings.companyWebsite}
                disclaimer={settings.disclaimer}
                logoPath={settings.logoPath}
                headerImage={settings.headerImage}
                footerImage={settings.footerImage}
                handleChange={handleChange}
              />
              <ReportSettings
                colorScheme={settings.colorScheme}
                handleChange={handleChange}
              />
              <SpreadsheetSettings
                enableAIRewriting={settings.enableAIRewriting}
                handleChange={handleChange}
              />
              <AdminKeySettings userId={userid} />
            </>
          )}

          {settings.activeTab === 'advisor' && (
            <AdvisorSettings
              advisors={settings.advisors}
              handleAdvisorChange={handleAdvisorChange}
              availableDesignations={availableDesignations}
              addAdvisor={addAdvisor}
            />
          )}

          {settings.activeTab === 'company' && (
            <>
              <CompanySettings
                companyName={settings.companyName}
                companyAddress={settings.companyAddress}
                companyPhone={settings.companyPhone}
                companyWebsite={settings.companyWebsite}
                disclaimer={settings.disclaimer}
                handleChange={handleChange}
                handleFileUpload={handleFileUpload}
                logoPath={settings.logoPath}
              />
              <DocumentImageSettings
                companyName={settings.companyName}
                companyAddress={settings.companyAddress}
                companyPhone={settings.companyPhone}
                companyWebsite={settings.companyWebsite}
                disclaimer={settings.disclaimer}
                logoPath={settings.logoPath}
                headerImage={settings.headerImage}
                footerImage={settings.footerImage}
                handleChange={handleChange}
              />
            </>
          )}

          {settings.activeTab === 'report' && (
            <ReportSettings
              colorScheme={settings.colorScheme}
              handleChange={handleChange}
            />
          )}

          {settings.activeTab === 'spreadsheet' && (
            <SpreadsheetSettings
              enableAIRewriting={settings.enableAIRewriting}
              handleChange={handleChange}
            />
          )}

          <Button 
            onClick={handleSaveSettings} 
            type="primary" 
            size="large"
            icon={<SaveOutlined />}
            className="px-10 h-14 text-lg bg-gradient-to-bl from-sky-400 to-blue-800 border-none shadow-lg hover:shadow-xl transition-all duration-300 rounded-full mt-8"
          >
            Save All Settings
          </Button>
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-12 text-right"
      >
        {/* Additional Content if Needed */}
      </motion.div>
    </AnimatedWrapper>
  );
};

export default Settings;
